/**
 * Pricing Service - Handles all pricing data from backend API
 * 
 * This service loads pricing data from the backend which reads CSV files
 * Automatically reloads when CSV files are updated on the server
 */

class PricingService {
    constructor() {
        this.pricingData = null;
        this.isLoading = false;
        this.lastFetch = null;
        this.fetchPromise = null; // Track ongoing fetch to prevent duplicates
    }

    /**
     * Fetch pricing data from backend API (with deduplication)
     */
    async fetchPricingData(forceRefresh = false) {
        // Return cached data if available and not forcing refresh
        if (!forceRefresh && this.pricingData && this.lastFetch) {
            const cacheAge = Date.now() - this.lastFetch;
            // Cache for 5 minutes (CSV files are auto-reloaded by backend)
            if (cacheAge < 5 * 60 * 1000) {
                return this.pricingData;
            }
        }

        // If already fetching, return the existing promise
        if (this.isLoading && this.fetchPromise) {
            return this.fetchPromise;
        }

        // Start new fetch
        this.isLoading = true;
        this.fetchPromise = this._loadFromAPI(forceRefresh);

        try {
            const data = await this.fetchPromise;
            return data;
        } finally {
            this.isLoading = false;
            this.fetchPromise = null;
        }
    }

    async _loadFromAPI(forceRefresh = false) {
        try {
            
            // Load CSV files from public folder (Vite copies this to build automatically)
            const [standaardData, pergamonData, optiesData] = await Promise.all([
                this._parseCSV('Pricelist/Standaard.csv'),
                this._parseCSV('Pricelist/Pergamon.csv'),
                this._parseCSV('Pricelist/Opties.csv')
            ]);

            this.pricingData = {
                Standaard: standaardData,
                Pergamon: pergamonData,
                Opties: optiesData
            };
            this.lastFetch = Date.now();
            
            return this.pricingData;
            
        } catch (error) {
            console.error('❌ Error loading pricing data:', error);
            // Return cached data if available, even if stale
            if (this.pricingData) {
                console.warn('⚠️ Using stale cached data due to fetch error');
                return this.pricingData;
            }
            throw error;
        }
    }

    async _parseCSV(filePath) {
        const response = await fetch(filePath);
        
        if (!response.ok) {
            throw new Error(`Failed to load ${filePath}: ${response.status}`);
        }
        
        const text = await response.text();
        
        // Better CSV parsing that handles multi-line quoted fields
        const result = [];
        let currentRow = [];
        let currentField = '';
        let inQuotes = false;
        let headerRow = null;
        
        for (let i = 0; i < text.length; i++) {
            const char = text[i];
            const nextChar = text[i + 1];
            
            if (char === '"') {
                if (inQuotes && nextChar === '"') {
                    // Escaped quote
                    currentField += '"';
                    i++; // Skip next quote
                } else {
                    // Toggle quote state
                    inQuotes = !inQuotes;
                }
            } else if (char === ',' && !inQuotes) {
                // End of field
                currentRow.push(currentField.trim());
                currentField = '';
            } else if (char === '\n' && !inQuotes) {
                // End of row
                currentRow.push(currentField.trim());
                
                if (!headerRow) {
                    headerRow = currentRow;
                } else if (currentRow.some(field => field.length > 0)) {
                    // Create object from row
                    const obj = {};
                    headerRow.forEach((header, index) => {
                        obj[header] = currentRow[index] || '';
                    });
                    result.push(obj);
                }
                
                currentRow = [];
                currentField = '';
            } else if (char === '\r') {
                // Skip carriage returns
                continue;
            } else {
                currentField += char;
            }
        }
        
        // Handle last row if no trailing newline
        if (currentRow.length > 0 || currentField.length > 0) {
            currentRow.push(currentField.trim());
            if (headerRow && currentRow.some(field => field.length > 0)) {
                const obj = {};
                headerRow.forEach((header, index) => {
                    obj[header] = currentRow[index] || '';
                });
                result.push(obj);
            }
        }

        return result;
    }

    /**
     * Force refresh pricing data (clears cache and reloads)
     */
    async refreshPricingData() {
        try {
            
            // Clear local cache and reload
            this.clearCache();
            return await this.fetchPricingData(true);
            
        } catch (error) {
            console.error('❌ Error refreshing pricing data:', error);
            throw error;
        }
    }

    /**
     * Get price for specific width and depth from a sheet
     * @param {string} sheetName - Name of the sheet (e.g., 'Pergamon', 'Standaard')
     * @param {number} width - Width in meters
     * @param {number} depth - Depth in meters
     * @returns {number|null} - Price in euros or null if not found
     */
    getPrice(sheetName, width, depth) {
        
        if (!this.pricingData || !this.pricingData[sheetName]) {
            console.error(`❌ Geen pricing data voor sheet: ${sheetName}`);
            console.error('❌ FOUT: Kan geen basisprijs bepalen! Beschikbare sheets:', Object.keys(this.pricingData || {}));
            return null;
        }

        const sheet = this.pricingData[sheetName];
        
        // Convert CSV strings to numbers for comparison
        const targetWidth = Number(width);
        const targetDepth = Number(depth);
        
        // Try multiple matching strategies
        let match = null;
        let matchStrategy = '';
        
        // Strategy 1: Exact match
        match = sheet.find(row => {
            const rowWidth = Number(row.Breedte);
            const rowDepth = Number(row.Diepte);
            return rowWidth === targetWidth && rowDepth === targetDepth;
        });
        if (match) matchStrategy = 'Exacte match';

        // Strategy 2: If depth is half meter (like 2.5, 3.5), try as integer (25, 35)
        if (!match && targetDepth % 1 === 0.5) {
            const depthAsInt = Math.round(targetDepth * 10); // 2.5 -> 25, 3.5 -> 35
            match = sheet.find(row => {
                const rowWidth = Number(row.Breedte);
                const rowDepth = Number(row.Diepte);
                return rowWidth === targetWidth && rowDepth === depthAsInt;
            });
            if (match) matchStrategy = `Diepte als integer (${depthAsInt})`;
        }

        // Strategy 3: Loose matching (floating point comparison)
        if (!match) {
            match = sheet.find(row => {
                const rowWidth = Number(row.Breedte);
                const rowDepth = Number(row.Diepte);
                const widthMatch = Math.abs(rowWidth - targetWidth) < 0.01;
                const depthMatch = Math.abs(rowDepth - targetDepth) < 0.01;
                if (widthMatch && depthMatch) {
                }
                return widthMatch && depthMatch;
            });
            if (match) matchStrategy = 'Loose match (tolerantie)';
        }

        // Strategy 4: Try depth with comma notation in CSV (3,5 instead of 3.5)
        if (!match && targetDepth % 1 === 0.5) {
            const depthStr = targetDepth.toString().replace('.', ','); // 3.5 -> "3,5"
            match = sheet.find(row => {
                const rowWidth = Number(row.Breedte);
                const rowDepthStr = String(row.Diepte);
                return rowWidth === targetWidth && rowDepthStr === depthStr;
            });
            if (match) matchStrategy = `Diepte met komma (${depthStr})`;
        }

        if (!match) {
            console.error(`❌ GEEN PRIJS GEVONDEN voor ${sheetName}: ${width}m x ${depth}m`);
            console.error('❌ FOUT: Dit mag NOOIT gebeuren! Check CSV data.');
            console.error('💡 Beschikbare combinaties in CSV:', sheet.map(r => `${r.Breedte}x${r.Diepte}`).slice(0, 10));
            return null;
        }

        // Parse price string: "€ 3.378,70" -> 3378.70
        const priceStr = match.Prijs;
        const price = this._parsePrice(priceStr);
        
        return price;
    }

    /**
     * Parse Dutch-formatted price string to number
     * "€ 3.378,70" -> 3378.70
     */
    _parsePrice(priceStr) {
        if (!priceStr) return 0;
        
        // Remove € symbol and spaces
        let cleaned = priceStr.replace(/€/g, '').trim();
        
        // Replace dots (thousands separator) with nothing
        cleaned = cleaned.replace(/\./g, '');
        
        // Replace comma (decimal separator) with dot
        cleaned = cleaned.replace(/,/g, '.');
        
        return parseFloat(cleaned) || 0;
    }

    /**
     * Format price for display in Dutch format
     * 3378.70 -> "3.378,70"
     */
    formatPrice(price) {
        if (!price) return '0,00';
        
        const parts = price.toFixed(2).split('.');
        const integerPart = parts[0];
        const decimalPart = parts[1];
        
        // Add thousands separator
        const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        
        return `${formattedInteger},${decimalPart}`;
    }

    /**
     * Get all available sheet names
     */
    getAvailableSheets() {
        if (!this.pricingData) return [];
        return Object.keys(this.pricingData);
    }

    /**
     * Check if pricing data is loaded
     */
    isDataLoaded() {
        return this.pricingData !== null;
    }

    /**
     * Clear cached data
     */
    clearCache() {
        this.pricingData = null;
        this.lastFetch = null;
    }
}

// Create singleton instance
const pricingService = new PricingService();

// Export for use in other modules
export default pricingService;
